import { Controller } from "@hotwired/stimulus"
import Litepicker from "litepicker"

export default class extends Controller {
  static targets = [
    "dateRange", "checkIn", "checkOut",
    "staticRoomType", "roomType",
    "roomCards", "roomCardsList", "roomTypeHidden",
    "rateDisplay", "rateText",
    "loading", "snapshot",
    "roomDetails", "guestsInput", "roomsInput", "roomsCountHidden", "guestsCountHidden", "totalDisplay",
    "pmsRoomTypeIdHidden"
  ]
  static values = { url: String }

  connect() {
    this.selectedRoom = null
    this.availabilityData = null
    this.selectedRoomRate = null
    this.selectedRoomName = null
    this.selectedRoomAvailable = 0
    this.selectedRoomMaxAdults = 2
    this.selectedRoomTypeId = ""
    this.diveDate = null
    this.initPicker()
    this.restoreSelection()
    this.bindDiveDateInput()
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
      this.picker = null
    }
    this.unbindDiveDateInput()
  }

  bindDiveDateInput() {
    const form = this.element.closest("form")
    this._diveDateInput = form?.querySelector('input[name="booking[preferred_date]"]')
    if (this._diveDateInput) {
      this.diveDate = this._diveDateInput.value || null
      this._diveDateChangeHandler = () => {
        this.diveDate = this._diveDateInput.value || null
        this.validateAgainstDiveDate()
      }
      this._diveDateInput.addEventListener("change", this._diveDateChangeHandler)
    }
  }

  unbindDiveDateInput() {
    if (this._diveDateInput && this._diveDateChangeHandler) {
      this._diveDateInput.removeEventListener("change", this._diveDateChangeHandler)
    }
  }

  initPicker() {
    if (!this.hasDateRangeTarget) return

    this.picker = new Litepicker({
      element: this.dateRangeTarget,
      singleMode: false,
      numberOfMonths: window.innerWidth >= 768 ? 2 : 1,
      numberOfColumns: window.innerWidth >= 768 ? 2 : 1,
      minDate: new Date(),
      format: "MMM D, YYYY",
      delimiter: "  \u2014  ",
      tooltipText: { one: "night", other: "nights" },
      autoApply: true,
      showTooltip: true,
      mobileFriendly: true,
      setup: (picker) => {
        picker.on("selected", (start, end) => {
          if (start && end) {
            if (this.diveDate) {
              const diveDateObj = new Date(this.diveDate + "T00:00:00")
              const startDate = start.dateInstance
              const endDate = end.dateInstance

              if (startDate > diveDateObj || endDate < diveDateObj) {
                this.showDateConstraintError(diveDateObj)
                setTimeout(() => {
                  this.picker.clearSelection()
                  this.dateRangeTarget.value = ""
                  this.checkInTarget.value = ""
                  this.checkOutTarget.value = ""
                }, 0)
                return
              }
            }
            this.hideDateConstraintError()
            this.checkInTarget.value = this.formatDateISO(start.dateInstance)
            this.checkOutTarget.value = this.formatDateISO(end.dateInstance)
            this.fetchAvailability()
          }
        })
      }
    })
  }

  restoreSelection() {
    if (this.hasCheckInTarget && this.hasCheckOutTarget &&
      this.checkInTarget.value && this.checkOutTarget.value) {
      const checkIn = new Date(this.checkInTarget.value + "T00:00:00")
      const checkOut = new Date(this.checkOutTarget.value + "T00:00:00")
      if (!isNaN(checkIn) && !isNaN(checkOut)) {
        this.picker.setDateRange(checkIn, checkOut)
      }
    }
  }

  openPicker() {
    if (this.picker) {
      this.picker.show()
    }
  }

  async fetchAvailability() {
    const checkIn = this.checkInTarget.value
    const checkOut = this.checkOutTarget.value
    if (!checkIn || !checkOut) return

    this.showLoading()
    this.hideRateDisplay()

    try {
      const response = await fetch(`${this.urlValue}?check_in=${checkIn}&check_out=${checkOut}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (data.success && data.rooms && data.rooms.length > 0) {
        this.availabilityData = data
        this.showRoomCards(data.rooms)
        this.storeSnapshot(data, checkIn, checkOut)
      } else {
        // eZee not available — keep static dropdown
        this.availabilityData = null
        this.showStaticDropdown()
        this.clearSnapshot()
      }
    } catch (error) {
      console.warn("Availability check failed:", error.message)
      this.availabilityData = null
      this.showStaticDropdown()
      this.clearSnapshot()
    } finally {
      this.hideLoading()
    }
  }

  // --- Card-based room display ---

  showRoomCards(rooms) {
    if (!this.hasRoomCardsTarget || !this.hasRoomCardsListTarget) return

    const nights = this.calculateNights(this.checkInTarget.value, this.checkOutTarget.value)

    // Hide static dropdown, show cards
    if (this.hasStaticRoomTypeTarget) {
      this.staticRoomTypeTarget.classList.add("d-none")
      // Remove the name attribute so static dropdown doesn't submit
      this.roomTypeTarget.removeAttribute("name")
    }
    this.roomCardsTarget.classList.remove("d-none")

    // Enable the hidden input for room_type submission
    if (this.hasRoomTypeHiddenTarget) {
      this.roomTypeHiddenTarget.setAttribute("name", "booking[room_type]")
    }

    const currentSelection = this.selectedRoom ||
      (this.hasRoomTypeTarget ? this.roomTypeTarget.value : null)

    this.roomCardsListTarget.innerHTML = rooms.map((room, index) => {
      const slug = this.slugify(room.room_type_name || room.room_name || "room")
      const name = room.room_type_name || room.room_name || "Room"
      const available = (room.available || 0)
      const rate = room.rate ? parseFloat(room.rate) : null
      const currency = room.currency || "USD"
      const isSoldOut = available === 0
      const isSelected = slug === currentSelection || (!currentSelection && index === 0 && !isSoldOut)

      const total = rate ? (rate * nights).toFixed(2) : null

      // Availability badge
      const badgeStyle = isSoldOut
        ? "background: rgba(239, 68, 68, 0.15); color: #ef4444; border: 1px solid rgba(239, 68, 68, 0.3);"
        : "background: rgba(16, 185, 129, 0.15); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.3);"
      const badgeText = isSoldOut ? "Sold out" : `${available} available`

      return `
        <div class="room-card ${isSelected && !isSoldOut ? 'room-card--selected' : ''} ${isSoldOut ? 'room-card--sold-out' : ''}"
             data-slug="${this.escapeAttr(slug)}"
             data-rate="${rate || ''}"
             data-available="${available}"
             data-max-adults="${room.max_adults || 2}"
             data-room-type-id="${this.escapeAttr(room.room_type_id || '')}"
             data-room-name="${this.escapeAttr(name)}"
             data-action="${isSoldOut ? '' : 'click->accommodation-availability#pickRoom'}"
             role="${isSoldOut ? '' : 'button'}"
             tabindex="${isSoldOut ? '-1' : '0'}">
          <div class="room-card__header">
            <div class="room-card__name">
              ${isSelected && !isSoldOut ? '<i class="bi bi-check-circle-fill room-card__icon room-card__icon--active"></i>' : '<i class="bi bi-circle room-card__icon"></i>'}
              <span class="room-card__title">${this.escapeHtml(name)}</span>
            </div>
            <span class="room-card__badge" style="${badgeStyle}">${badgeText}</span>
          </div>
          ${rate ? `
          <div class="room-card__pricing">
            <span class="room-card__rate">${currency} ${rate.toFixed(2)}</span><span class="room-card__per-night">/night</span>${total ? `<span class="room-card__total">&middot; ${currency} ${total} for ${nights} night${nights > 1 ? 's' : ''}</span>` : ''}
          </div>` : ''}
        </div>
      `
    }).join("")

    // Auto-select first available room if nothing selected
    const firstAvailable = rooms.find(r => (r.available || 0) > 0)
    if (firstAvailable && !this.selectedRoom) {
      const slug = this.slugify(firstAvailable.room_type_name || firstAvailable.room_name || "room")
      this.setSelectedRoom(slug, firstAvailable.rate, firstAvailable.room_type_name)
    } else if (this.selectedRoom) {
      const match = rooms.find(r => this.slugify(r.room_type_name || r.room_name || "room") === this.selectedRoom)
      if (match && (match.available || 0) > 0) {
        this.updateRateDisplay(match.rate, match.room_type_name)
      }
    }
  }

  pickRoom(event) {
    const card = event.currentTarget
    const slug = card.dataset.slug
    const rate = card.dataset.rate ? parseFloat(card.dataset.rate) : null
    const name = card.dataset.roomName
    const available = parseInt(card.dataset.available) || 0
    const maxAdults = parseInt(card.dataset.maxAdults) || 2
    const roomTypeId = card.dataset.roomTypeId || ""

    this.selectedRoom = slug
    this.selectedRoomRate = rate
    this.selectedRoomName = name
    this.selectedRoomAvailable = available
    this.selectedRoomMaxAdults = maxAdults
    this.selectedRoomTypeId = roomTypeId

    // Update card visuals
    this.roomCardsListTarget.querySelectorAll(".room-card").forEach(c => {
      const isThis = c.dataset.slug === slug
      const icon = c.querySelector("i.bi")
      if (isThis) {
        c.classList.add("room-card--selected")
        if (icon) { icon.className = "bi bi-check-circle-fill room-card__icon room-card__icon--active" }
      } else {
        c.classList.remove("room-card--selected")
        if (icon) { icon.className = "bi bi-circle room-card__icon" }
      }
    })

    // Show room details section and scroll to it
    this.showRoomDetails()
  }

  setSelectedRoom(slug, rate, name) {
    this.selectedRoom = slug
    this.selectedRoomRate = rate ? parseFloat(rate) : null
    this.selectedRoomName = name

    // Find available count for this room
    if (this.availabilityData?.rooms) {
      const match = this.availabilityData.rooms.find(r =>
        this.slugify(r.room_type_name || r.room_name || "room") === slug
      )
      this.selectedRoomAvailable = match ? (match.available || 0) : 0
      this.selectedRoomMaxAdults = match ? (parseInt(match.max_adults) || 2) : 2
      this.selectedRoomTypeId = match ? (match.room_type_id || "") : ""
    }

    // Update hidden input (will be overwritten by confirmRoom if user adjusts)
    if (this.hasRoomTypeHiddenTarget) {
      this.roomTypeHiddenTarget.value = slug
    }
  }

  updateRateDisplay(rate, name, rooms = 1) {
    if (!rate || !this.hasRateDisplayTarget || !this.hasRateTextTarget) {
      this.hideRateDisplay()
      return
    }

    const nights = this.calculateNights(this.checkInTarget.value, this.checkOutTarget.value)
    const total = (parseFloat(rate) * nights * rooms).toFixed(2)

    const roomSuffix = rooms > 1 ? ` \u00d7 ${rooms} rooms` : ""
    this.rateTextTarget.textContent = `${name}: USD ${parseFloat(rate).toFixed(2)}/night \u00d7 ${nights} night${nights !== 1 ? "s" : ""}${roomSuffix} = USD ${total} total (incl. tax)`
    this.rateDisplayTarget.classList.remove("d-none")
  }

  showRoomDetails() {
    if (!this.hasRoomDetailsTarget) return

    // Set defaults
    if (this.hasGuestsInputTarget) this.guestsInputTarget.textContent = "2"
    if (this.hasRoomsInputTarget) this.roomsInputTarget.textContent = "1"

    this.roomDetailsTarget.classList.remove("d-none")
    this.updateTotal()

    // Scroll the room details into view within the modal
    this.roomDetailsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  updateTotal() {
    if (!this.hasTotalDisplayTarget || !this.selectedRoomRate) return

    const nights = this.calculateNights(this.checkInTarget.value, this.checkOutTarget.value)
    const rooms = parseInt(this.roomsInputTarget?.textContent) || 1
    const total = (this.selectedRoomRate * nights * rooms).toFixed(2)

    this.totalDisplayTarget.innerHTML =
      `${nights} night${nights > 1 ? "s" : ""} &times; ${rooms} room${rooms > 1 ? "s" : ""} &times; USD ${this.selectedRoomRate.toFixed(2)}<br>` +
      `<span class="fw-bold" style="font-size: 1.1rem;">TOTAL: USD ${total}</span>`
  }

  incrementGuests() {
    const el = this.guestsInputTarget
    const val = parseInt(el.textContent) || 1
    const rooms = parseInt(this.roomsInputTarget?.textContent) || 1
    const max = this.selectedRoomMaxAdults * rooms
    if (val < max) { el.textContent = val + 1; this.updateTotal() }
  }

  decrementGuests() {
    const el = this.guestsInputTarget
    const val = parseInt(el.textContent) || 2
    if (val > 1) { el.textContent = val - 1; this.updateTotal() }
  }

  incrementRooms() {
    const el = this.roomsInputTarget
    const val = parseInt(el.textContent) || 1
    if (val < this.selectedRoomAvailable) { el.textContent = val + 1; this.updateTotal() }
  }

  decrementRooms() {
    const el = this.roomsInputTarget
    const val = parseInt(el.textContent) || 1
    if (val > 1) {
      const newRooms = val - 1
      el.textContent = newRooms
      // Clamp guests to new max capacity
      const maxGuests = this.selectedRoomMaxAdults * newRooms
      const guests = parseInt(this.guestsInputTarget?.textContent) || 1
      if (guests > maxGuests) { this.guestsInputTarget.textContent = maxGuests }
      this.updateTotal()
    }
  }

  confirmRoom() {
    const slug = this.selectedRoom
    const rate = this.selectedRoomRate
    const name = this.selectedRoomName
    const guests = parseInt(this.guestsInputTarget?.textContent) || 2
    const rooms = parseInt(this.roomsInputTarget?.textContent) || 1

    // Set hidden inputs
    if (this.hasRoomTypeHiddenTarget) this.roomTypeHiddenTarget.value = slug
    if (this.hasRoomsCountHiddenTarget) this.roomsCountHiddenTarget.value = rooms
    if (this.hasGuestsCountHiddenTarget) this.guestsCountHiddenTarget.value = guests
    if (this.hasPmsRoomTypeIdHiddenTarget) this.pmsRoomTypeIdHiddenTarget.value = this.selectedRoomTypeId || ""

    this.updateRateDisplay(rate, name, rooms)
    this.storeSnapshot(this.availabilityData, this.checkInTarget.value, this.checkOutTarget.value)
  }

  // --- Fallback to static dropdown ---

  showStaticDropdown() {
    if (this.hasStaticRoomTypeTarget) {
      this.staticRoomTypeTarget.classList.remove("d-none")
      this.roomTypeTarget.setAttribute("name", "booking[room_type]")
    }
    if (this.hasRoomCardsTarget) {
      this.roomCardsTarget.classList.add("d-none")
    }
    if (this.hasRoomTypeHiddenTarget) {
      this.roomTypeHiddenTarget.removeAttribute("name")
    }
  }

  // --- Snapshot ---

  storeSnapshot(data, checkIn, checkOut) {
    if (!this.hasSnapshotTarget) return
    const rooms = parseInt(this.roomsInputTarget?.textContent) || 1
    const guests = parseInt(this.guestsInputTarget?.textContent) || 2
    this.snapshotTarget.value = JSON.stringify({
      timestamp: new Date().toISOString(),
      check_in: checkIn,
      check_out: checkOut,
      selected_rooms_count: rooms,
      selected_guests_count: guests,
      rooms: data.rooms.map(room => ({
        room_type_name: room.room_type_name,
        available: room.available,
        rate: room.rate,
        currency: room.currency || "USD"
      }))
    })
  }

  clearSnapshot() {
    if (this.hasSnapshotTarget) this.snapshotTarget.value = ""
  }

  // --- UI helpers ---

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("d-none")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("d-none")
  }

  hideRateDisplay() {
    if (this.hasRateDisplayTarget) this.rateDisplayTarget.classList.add("d-none")
  }

  // --- Dive date constraint ---

  validateAgainstDiveDate() {
    if (!this.diveDate || !this.checkInTarget.value || !this.checkOutTarget.value) {
      this.hideDateConstraintError()
      return
    }

    const diveDateObj = new Date(this.diveDate + "T00:00:00")
    const checkIn = new Date(this.checkInTarget.value + "T00:00:00")
    const checkOut = new Date(this.checkOutTarget.value + "T00:00:00")

    if (checkIn > diveDateObj || checkOut < diveDateObj) {
      this.showDateConstraintError(diveDateObj)
      this.picker.clearSelection()
      this.dateRangeTarget.value = ""
      this.checkInTarget.value = ""
      this.checkOutTarget.value = ""
      this.hideRateDisplay()
    } else {
      this.hideDateConstraintError()
    }
  }

  showDateConstraintError(diveDate) {
    this.hideDateConstraintError()
    const msg = document.createElement("div")
    msg.className = "date-constraint-error invalid-feedback d-block"
    msg.textContent = `Your stay must include your diving date (${this.formatDisplayDate(diveDate)}). Check-in must be on or before, and check-out on or after this date.`
    this.dateRangeTarget.parentNode.appendChild(msg)
  }

  hideDateConstraintError() {
    const existing = this.element.querySelector(".date-constraint-error")
    if (existing) existing.remove()
  }

  formatDisplayDate(date) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  // --- Utilities ---

  formatDateISO(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  calculateNights(checkIn, checkOut) {
    const s = new Date(checkIn + "T00:00:00")
    const e = new Date(checkOut + "T00:00:00")
    return Math.max(1, Math.round((e - s) / 86400000))
  }

  slugify(text) {
    return text.toLowerCase().replace(/[&]/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
